import type { EvaluationMessageScore } from "../engines/evaluator-engine";
import type { ChessColor } from "../models/base-model";

/**
 * Converts a Stockfish evaluation score (centipawn or mate) into a normalized number
 * where higher values are always better for the given side.
 *
 * - Centipawn (cp) scores are used as-is for white, and negated for black.
 * - Mate scores are mapped to large positive (for white) or negative (for black) numbers,
 *   with closer mates being higher in value (e.g., mate in 1 is better than mate in 3).
 *   This is done by subtracting the mate distance from 100000 (for white) or -100000 (for black),
 *   so that mate scores are always much larger in magnitude than any centipawn value.
 * - This normalization allows direct numeric comparison of evaluations, regardless of type.
 *
 * @param score The evaluation score from Stockfish, either centipawn or mate.
 * @param side The perspective ("w" for white, "b" for black) to normalize for.
 * @returns A normalized numeric value where higher is always better for the given side.
 *
 * Example:
 *   normalizeScore({ type: "cp", score: 50 }, "w") // 50
 *   normalizeScore({ type: "cp", score: 50 }, "b") // -50
 *   normalizeScore({ type: "mate", score: 3 }, "w") // 99997
 *   normalizeScore({ type: "mate", score: -2 }, "b") // 99998
 */
const normalizeScore = (
    score: EvaluationMessageScore,
    side: ChessColor
): number => {
    let value: number;
    if (score.type === "cp") {
        value = score.score;
    } else if (score.type === "mate") {
        // Map mate scores to very large numbers so that any mate is always better than any cp value.
        // For white: mate in N => 100000 - N (e.g., mate in 3 => 99997)
        // For black: mate in -N => -100000 - (-N) = -100000 + N (e.g., mate in -2 => -99998)
        // This ensures mate scores are always much larger in magnitude than centipawn scores,
        // and that closer mates are higher in value.
        value = score.score > 0 ? 100000 - score.score : -100000 - score.score;
    } else {
        value = 0;
    }
    const norm = side === "w" ? value : -value;
    // Normalize -0 to 0 for consistency
    return Object.is(norm, -0) ? 0 : norm;
};

export type CompareScoreResult = "better" | "worse" | "equal";
export type CompareScoreChangeType =
    | "cp-to-cp"
    | "cp-to-mate"
    | "mate-to-cp"
    | "mate-to-mate";

export type CompareScoreReturn = {
    result: CompareScoreResult;
    diff: number;
    absDiff: number;
    oldNorm: number;
    newNorm: number;
    threshold: number;
    changeType: CompareScoreChangeType;
};

export type CompareScoreOptions = {
    oldScore: EvaluationMessageScore;
    newScore: EvaluationMessageScore;
    side: ChessColor;
    // Threshold within which scores are considered equal
    threshold?: number; // Optional, default 0
};

export const compareScore = ({
    oldScore,
    newScore,
    side,
    threshold = 0,
}: CompareScoreOptions): CompareScoreReturn => {
    const oldNorm = normalizeScore(oldScore, side);
    const newNorm = normalizeScore(newScore, side);
    const diff = newNorm - oldNorm;
    const absDiff = Math.abs(diff);

    let changeType: CompareScoreChangeType;
    if (oldScore.type === "cp" && newScore.type === "cp") {
        changeType = "cp-to-cp";
    } else if (oldScore.type === "cp" && newScore.type === "mate") {
        changeType = "cp-to-mate";
    } else if (oldScore.type === "mate" && newScore.type === "cp") {
        changeType = "mate-to-cp";
    } else {
        changeType = "mate-to-mate";
    }

    let result: CompareScoreResult;
    if (absDiff <= threshold) {
        result = "equal";
    } else if (diff > 0) {
        result = "better";
    } else {
        result = "worse";
    }

    return {
        result,
        diff,
        absDiff,
        oldNorm,
        newNorm,
        threshold,
        changeType,
    };
};

export const EvaluationComparerUtil = { compareScore };
